import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserPlus, Building2, Briefcase, CheckCircle, ChevronRight, ChevronLeft,
  Shield, FileText, Bot, Settings, Cpu, Activity, AlertCircle, Loader2,
  Users, Brain, Lock, Zap, Eye, Sparkles, Check, X, Plus, Minus
} from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import GlowingCard from '../components/GlowingCard';
import {
  getRoleTemplates,
  startOnboarding,
  selectRole,
  submitEmployeeDetails,
  submitResponsibilities,
  submitCapabilities,
  previewGovernanceFiles,
  confirmGovernance,
  setupCompliance,
  configureSubAgents,
  completeOnboarding
} from '../lib/api';

const STEPS = [
  { id: 'company', name: 'Company', icon: Building2 },
  { id: 'role', name: 'Role Selection', icon: Briefcase },
  { id: 'details', name: 'Employee Details', icon: UserPlus },
  { id: 'responsibilities', name: 'Responsibilities', icon: CheckCircle },
  { id: 'capabilities', name: 'Capabilities', icon: Brain },
  { id: 'governance', name: 'Governance Files', icon: FileText },
  { id: 'compliance', name: 'Compliance', icon: Shield },
  { id: 'subagents', name: 'Sub-Agents', icon: Bot },
  { id: 'review', name: 'Review & Activate', icon: Sparkles }
];

const ROLE_CATEGORIES = [
  { id: 'ENGINEERING', name: 'Engineering', icon: Cpu },
  { id: 'PRODUCT', name: 'Product', icon: Briefcase },
  { id: 'CUSTOMER_SUCCESS', name: 'Customer Success', icon: Users },
  { id: 'MARKETING', name: 'Marketing', icon: Zap },
  { id: 'SALES', name: 'Sales', icon: Activity },
  { id: 'FINANCE', name: 'Finance', icon: Building2 },
  { id: 'HR', name: 'HR', icon: Users },
  { id: 'LEGAL', name: 'Legal', icon: Shield },
  { id: 'SECURITY', name: 'Security', icon: Lock },
  { id: 'DATA_SCIENCE', name: 'Data Science', icon: Brain },
  { id: 'OPERATIONS', name: 'Operations', icon: Settings },
  { id: 'GENERAL', name: 'General', icon: Bot }
];

const COMPLIANCE_FRAMEWORKS = [
  { id: 'SOC2', name: 'SOC 2', description: 'Service Organization Control 2' },
  { id: 'GDPR', name: 'GDPR', description: 'General Data Protection Regulation' },
  { id: 'HIPAA', name: 'HIPAA', description: 'Health Insurance Portability and Accountability' },
  { id: 'ISO27001', name: 'ISO 27001', description: 'Information Security Management' }
];

const AUTONOMY_TIERS = [
  { id: 'FULL_AUTONOMY', name: 'Full Autonomy', description: 'Can execute decisions independently', risk: 'high' },
  { id: 'SUPERVISED', name: 'Supervised', description: 'Actions logged and reviewed', risk: 'medium' },
  { id: 'HUMAN_IN_LOOP', name: 'Human-in-Loop', description: 'Requires approval for actions', risk: 'low' },
  { id: 'RESTRICTED', name: 'Restricted', description: 'Limited to predefined actions', risk: 'minimal' }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Session state
  const [sessionToken, setSessionToken] = useState(null);

  // Form data
  const [companyData, setCompanyData] = useState({
    companyName: '',
    companyIndustry: '',
    companySize: 'MEDIUM'
  });

  const [roleTemplates, setRoleTemplates] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const [employeeDetails, setEmployeeDetails] = useState({
    name: '',
    title: '',
    department: '',
    description: ''
  });

  const [responsibilities, setResponsibilities] = useState([]);
  const [newResponsibility, setNewResponsibility] = useState('');

  const [capabilities, setCapabilities] = useState([]);
  const [newCapability, setNewCapability] = useState('');
  const [autonomyTier, setAutonomyTier] = useState('SUPERVISED');
  const [riskLevel, setRiskLevel] = useState('MEDIUM');

  const [governanceFiles, setGovernanceFiles] = useState({});
  const [expandedFile, setExpandedFile] = useState(null);

  const [selectedFrameworks, setSelectedFrameworks] = useState(['SOC2', 'GDPR']);

  const [recommendedSubAgents, setRecommendedSubAgents] = useState([]);
  const [selectedSubAgents, setSelectedSubAgents] = useState([]);

  const [provisionedEmployee, setProvisionedEmployee] = useState(null);

  // Load role templates on mount
  useEffect(() => {
    loadRoleTemplates();
  }, []);

  const loadRoleTemplates = async () => {
    try {
      const result = await getRoleTemplates();
      setRoleTemplates(result.templates);
    } catch (err) {
      console.error('Failed to load role templates:', err);
    }
  };

  const handleCompanySubmit = async () => {
    if (!companyData.companyName.trim()) {
      setError('Company name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await startOnboarding(companyData);
      setSessionToken(result.sessionToken);
      setCurrentStep(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = async () => {
    if (!selectedRole) {
      setError('Please select a role');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await selectRole(sessionToken, selectedRole.name);
      setRecommendedSubAgents(result.recommendedSubAgents || []);
      setSelectedSubAgents(result.recommendedSubAgents || []);

      // Pre-fill from template
      setEmployeeDetails(prev => ({
        ...prev,
        title: selectedRole.name.replace(' Agent', ''),
        description: selectedRole.description
      }));
      setResponsibilities([...selectedRole.defaultResponsibilities]);
      setCapabilities([...selectedRole.defaultCapabilities]);
      setAutonomyTier(selectedRole.suggestedAutonomyTier);
      setRiskLevel(selectedRole.riskProfile);

      setCurrentStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDetailsSubmit = async () => {
    if (!employeeDetails.name.trim() || !employeeDetails.title.trim()) {
      setError('Name and title are required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await submitEmployeeDetails(sessionToken, employeeDetails);
      setCurrentStep(3);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResponsibilitiesSubmit = async () => {
    if (responsibilities.length === 0) {
      setError('At least one responsibility is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await submitResponsibilities(sessionToken, responsibilities);
      setCurrentStep(4);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCapabilitiesSubmit = async () => {
    if (capabilities.length === 0) {
      setError('At least one capability is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await submitCapabilities(sessionToken, { capabilities, autonomyTier, riskLevel });
      // Generate governance preview
      const preview = await previewGovernanceFiles(sessionToken, selectedFrameworks);
      setGovernanceFiles(preview.fullFiles);
      setCurrentStep(5);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGovernanceConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      await confirmGovernance(sessionToken);
      setCurrentStep(6);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplianceSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      await setupCompliance(sessionToken, { complianceFrameworks: selectedFrameworks });
      setCurrentStep(7);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubAgentsSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      await configureSubAgents(sessionToken, selectedSubAgents);
      setCurrentStep(8);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await completeOnboarding(sessionToken);
      setProvisionedEmployee(result.employee);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addResponsibility = () => {
    if (newResponsibility.trim()) {
      setResponsibilities([...responsibilities, newResponsibility.trim()]);
      setNewResponsibility('');
    }
  };

  const removeResponsibility = (index) => {
    setResponsibilities(responsibilities.filter((_, i) => i !== index));
  };

  const addCapability = () => {
    if (newCapability.trim()) {
      setCapabilities([...capabilities, newCapability.trim()]);
      setNewCapability('');
    }
  };

  const removeCapability = (index) => {
    setCapabilities(capabilities.filter((_, i) => i !== index));
  };

  const toggleSubAgent = (agent) => {
    const isSelected = selectedSubAgents.some(a => a.type === agent.type);
    if (isSelected) {
      setSelectedSubAgents(selectedSubAgents.filter(a => a.type !== agent.type));
    } else {
      setSelectedSubAgents([...selectedSubAgents, agent]);
    }
  };

  const filteredRoles = selectedCategory
    ? roleTemplates.filter(r => r.category === selectedCategory)
    : roleTemplates;

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Company
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Company Information</h2>
              <p className="text-gray-400">
                Tell us about your organization to customize the digital employee experience.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={companyData.companyName}
                  onChange={(e) => setCompanyData({ ...companyData, companyName: e.target.value })}
                  className="w-full px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Industry
                </label>
                <input
                  type="text"
                  value={companyData.companyIndustry}
                  onChange={(e) => setCompanyData({ ...companyData, companyIndustry: e.target.value })}
                  className="w-full px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                  placeholder="e.g., Technology, Healthcare, Finance"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Company Size
                </label>
                <select
                  value={companyData.companySize}
                  onChange={(e) => setCompanyData({ ...companyData, companySize: e.target.value })}
                  className="w-full px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                >
                  <option value="STARTUP">Startup (1-10)</option>
                  <option value="SMALL">Small (11-50)</option>
                  <option value="MEDIUM">Medium (51-200)</option>
                  <option value="LARGE">Large (201-1000)</option>
                  <option value="ENTERPRISE">Enterprise (1000+)</option>
                </select>
              </div>
            </div>

            <button
              onClick={handleCompanySubmit}
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        );

      case 1: // Role Selection
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Select Employee Role</h2>
              <p className="text-gray-400">
                Choose a role template that best matches your needs. You can customize it later.
              </p>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${!selectedCategory
                    ? 'bg-cao-blue text-white'
                    : 'bg-gray-800 text-gray-400 hover:text-white'
                  }`}
              >
                All
              </button>
              {ROLE_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors flex items-center gap-1 ${selectedCategory === cat.id
                      ? 'bg-cao-blue text-white'
                      : 'bg-gray-800 text-gray-400 hover:text-white'
                    }`}
                >
                  <cat.icon className="w-3 h-3" />
                  {cat.name}
                </button>
              ))}
            </div>

            {/* Role Cards */}
            <div className="grid gap-3 max-h-96 overflow-y-auto pr-2">
              {filteredRoles.map((role) => (
                <button
                  key={role.name}
                  onClick={() => setSelectedRole(role)}
                  className={`p-4 rounded-lg border text-left transition-all ${selectedRole?.name === role.name
                      ? 'border-cao-blue bg-cao-blue/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-white">{role.name}</h3>
                      <p className="text-sm text-gray-400 mt-1 line-clamp-2">{role.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs px-2 py-0.5 bg-gray-700 rounded text-gray-300">
                          {role.category}
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${role.riskProfile === 'LOW' ? 'bg-green-900/50 text-green-400' :
                            role.riskProfile === 'MEDIUM' ? 'bg-yellow-900/50 text-yellow-400' :
                              role.riskProfile === 'HIGH' ? 'bg-orange-900/50 text-orange-400' :
                                'bg-red-900/50 text-red-400'
                          }`}>
                          {role.riskProfile} Risk
                        </span>
                      </div>
                    </div>
                    {selectedRole?.name === role.name && (
                      <Check className="w-5 h-5 text-cao-blue flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(0)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleRoleSelect}
                disabled={loading || !selectedRole}
                className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Continue with {selectedRole?.name || 'Selected Role'}
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        );

      case 2: // Employee Details
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Employee Details</h2>
              <p className="text-gray-400">
                Define the identity and basic information for your digital employee.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Employee Name *
                </label>
                <input
                  type="text"
                  value={employeeDetails.name}
                  onChange={(e) => setEmployeeDetails({ ...employeeDetails, name: e.target.value })}
                  className="w-full px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                  placeholder="e.g., Atlas, Nova, Sentinel"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Job Title *
                </label>
                <input
                  type="text"
                  value={employeeDetails.title}
                  onChange={(e) => setEmployeeDetails({ ...employeeDetails, title: e.target.value })}
                  className="w-full px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                  placeholder="e.g., Senior Software Development Agent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Department
                </label>
                <input
                  type="text"
                  value={employeeDetails.department}
                  onChange={(e) => setEmployeeDetails({ ...employeeDetails, department: e.target.value })}
                  className="w-full px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                  placeholder="e.g., Engineering, Product, Marketing"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Role Description
                </label>
                <textarea
                  value={employeeDetails.description}
                  onChange={(e) => setEmployeeDetails({ ...employeeDetails, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue resize-none"
                  placeholder="Describe what this digital employee will do..."
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(1)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleDetailsSubmit}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        );

      case 3: // Responsibilities
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Define Responsibilities</h2>
              <p className="text-gray-400">
                Specify the key responsibilities this digital employee will handle.
              </p>
            </div>

            <div className="space-y-3">
              {responsibilities.map((resp, index) => (
                <div key={index} className="flex items-start gap-2 p-3 bg-gray-800/50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-cao-green flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 flex-1">{resp}</span>
                  <button
                    onClick={() => removeResponsibility(index)}
                    className="text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newResponsibility}
                onChange={(e) => setNewResponsibility(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addResponsibility()}
                className="flex-1 px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                placeholder="Add a responsibility..."
              />
              <button
                onClick={addResponsibility}
                className="px-4 py-3 bg-cao-blue text-white rounded-lg hover:bg-cao-blue/80 transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleResponsibilitiesSubmit}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        );

      case 4: // Capabilities
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Capabilities & Autonomy</h2>
              <p className="text-gray-400">
                Define capabilities and set the autonomy level for your digital employee.
              </p>
            </div>

            <div className="space-y-3 max-h-48 overflow-y-auto">
              {capabilities.map((cap, index) => (
                <div key={index} className="flex items-start gap-2 p-3 bg-gray-800/50 rounded-lg">
                  <Zap className="w-5 h-5 text-cao-purple flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 flex-1">{cap}</span>
                  <button
                    onClick={() => removeCapability(index)}
                    className="text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <input
                type="text"
                value={newCapability}
                onChange={(e) => setNewCapability(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addCapability()}
                className="flex-1 px-4 py-3 bg-cao-dark border border-gray-700 rounded-lg text-white focus:border-cao-blue focus:ring-1 focus:ring-cao-blue"
                placeholder="Add a capability..."
              />
              <button
                onClick={addCapability}
                className="px-4 py-3 bg-cao-purple text-white rounded-lg hover:bg-cao-purple/80 transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Autonomy Tier
              </label>
              <div className="grid grid-cols-2 gap-3">
                {AUTONOMY_TIERS.map((tier) => (
                  <button
                    key={tier.id}
                    onClick={() => setAutonomyTier(tier.id)}
                    className={`p-3 rounded-lg border text-left transition-all ${autonomyTier === tier.id
                        ? 'border-cao-blue bg-cao-blue/10'
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                      }`}
                  >
                    <div className="font-medium text-white">{tier.name}</div>
                    <div className="text-xs text-gray-400 mt-1">{tier.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(3)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleCapabilitiesSubmit}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Generate Governance Files
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        );

      case 5: // Governance Files
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Governance Files Generated</h2>
              <p className="text-gray-400">
                Review the generated governance files that will define your digital employee's behavior.
              </p>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
              {Object.entries(governanceFiles).map(([type, content]) => (
                <div key={type} className="border border-gray-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedFile(expandedFile === type ? null : type)}
                    className="w-full p-3 bg-gray-800/50 flex items-center justify-between hover:bg-gray-800 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-cao-blue" />
                      <span className="font-medium text-white">{type}.md</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">
                        {Math.round(content.length / 1024)}KB
                      </span>
                      <Eye className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                  {expandedFile === type && (
                    <div className="p-4 bg-cao-dark border-t border-gray-700 max-h-64 overflow-y-auto">
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">
                        {content}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="p-4 bg-cao-blue/10 border border-cao-blue/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-cao-blue flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-300">
                    These files establish the identity, boundaries, and operating procedures
                    for your digital employee. They can be updated later through the governance dashboard.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(4)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleGovernanceConfirm}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Approve & Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        );

      case 6: // Compliance
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Compliance Frameworks</h2>
              <p className="text-gray-400">
                Select the regulatory frameworks your digital employee should comply with.
              </p>
            </div>

            <div className="grid gap-3">
              {COMPLIANCE_FRAMEWORKS.map((framework) => (
                <button
                  key={framework.id}
                  onClick={() => {
                    if (selectedFrameworks.includes(framework.id)) {
                      setSelectedFrameworks(selectedFrameworks.filter(f => f !== framework.id));
                    } else {
                      setSelectedFrameworks([...selectedFrameworks, framework.id]);
                    }
                  }}
                  className={`p-4 rounded-lg border text-left transition-all flex items-center justify-between ${selectedFrameworks.includes(framework.id)
                      ? 'border-cao-green bg-cao-green/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                >
                  <div>
                    <h3 className="font-semibold text-white">{framework.name}</h3>
                    <p className="text-sm text-gray-400">{framework.description}</p>
                  </div>
                  {selectedFrameworks.includes(framework.id) && (
                    <Check className="w-5 h-5 text-cao-green" />
                  )}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(5)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleComplianceSubmit}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Continue
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        );

      case 7: // Sub-Agents
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Configure Sub-Agents</h2>
              <p className="text-gray-400">
                Select specialized sub-agents to support your digital employee's work.
              </p>
            </div>

            <div className="grid gap-3 max-h-80 overflow-y-auto pr-2">
              {recommendedSubAgents.map((agent) => (
                <button
                  key={agent.type}
                  onClick={() => toggleSubAgent(agent)}
                  className={`p-4 rounded-lg border text-left transition-all ${selectedSubAgents.some(a => a.type === agent.type)
                      ? 'border-cao-purple bg-cao-purple/10'
                      : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-white flex items-center gap-2">
                        <Bot className="w-4 h-4" />
                        {agent.name}
                      </h3>
                      <p className="text-sm text-gray-400 mt-1">{agent.description}</p>
                      <p className="text-xs text-cao-purple mt-2">{agent.purpose}</p>
                    </div>
                    {selectedSubAgents.some(a => a.type === agent.type) && (
                      <Check className="w-5 h-5 text-cao-purple flex-shrink-0" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            <div className="p-4 bg-gray-800/50 rounded-lg">
              <p className="text-sm text-gray-400">
                <strong className="text-white">{selectedSubAgents.length}</strong> sub-agents selected.
                Sub-agents will be automatically provisioned and orchestrated.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(6)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleSubAgentsSubmit}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                Continue to Review
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        );

      case 8: // Review & Activate
        if (provisionedEmployee) {
          return (
            <div className="space-y-6 text-center">
              <div className="w-20 h-20 mx-auto bg-cao-green/20 rounded-full flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-cao-green" />
              </div>

              <div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  {provisionedEmployee.name} is Ready!
                </h2>
                <p className="text-gray-400">
                  Your digital employee has been successfully provisioned and is now active.
                </p>
              </div>

              <div className="p-4 bg-gray-800/50 rounded-lg text-left">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Employee ID</span>
                    <p className="text-white font-mono">{provisionedEmployee.employeeId}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Status</span>
                    <p className="text-cao-green font-medium">{provisionedEmployee.status}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Governance Files</span>
                    <p className="text-white">{provisionedEmployee.governanceFiles?.length || 0} files</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Sub-Agents</span>
                    <p className="text-white">{provisionedEmployee.subAgents?.length || 0} agents</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => navigate('/employees')}
                  className="flex-1 py-3 bg-gradient-to-r from-cao-blue to-cao-purple text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                >
                  View Employee Dashboard
                </button>
                <button
                  onClick={() => {
                    setCurrentStep(0);
                    setSessionToken(null);
                    setProvisionedEmployee(null);
                    setSelectedRole(null);
                    setEmployeeDetails({ name: '', title: '', department: '', description: '' });
                  }}
                  className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                >
                  Create Another
                </button>
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">Review & Activate</h2>
              <p className="text-gray-400">
                Review your configuration and activate your digital employee.
              </p>
            </div>

            <div className="space-y-4">
              <div className="p-4 bg-gray-800/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-2">Employee</h3>
                <p className="text-lg font-semibold text-white">{employeeDetails.name}</p>
                <p className="text-gray-400">{employeeDetails.title}</p>
              </div>

              <div className="p-4 bg-gray-800/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-2">Role</h3>
                <p className="text-white">{selectedRole?.name}</p>
                <p className="text-sm text-gray-500">{selectedRole?.category}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-800/50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-400 mb-1">Autonomy</h3>
                  <p className="text-white">{autonomyTier.replace('_', ' ')}</p>
                </div>
                <div className="p-4 bg-gray-800/50 rounded-lg">
                  <h3 className="text-sm font-medium text-gray-400 mb-1">Sub-Agents</h3>
                  <p className="text-white">{selectedSubAgents.length} configured</p>
                </div>
              </div>

              <div className="p-4 bg-gray-800/50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-400 mb-2">Compliance</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedFrameworks.map(f => (
                    <span key={f} className="px-2 py-1 bg-cao-blue/20 text-cao-blue text-xs rounded">
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 bg-cao-purple/10 border border-cao-purple/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-cao-purple flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-300">
                    Upon activation, the governance engine will start, sub-agents will be initialized,
                    and background jobs will begin running according to schedule.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setCurrentStep(7)}
                className="px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                Back
              </button>
              <button
                onClick={handleComplete}
                disabled={loading}
                className="flex-1 py-3 bg-gradient-to-r from-cao-green to-cao-blue text-white rounded-lg font-semibold hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
                Activate Digital Employee
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen pt-20 pb-12 relative">
      <AnimatedBackground />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between overflow-x-auto pb-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = index === currentStep;
              const isCompleted = index < currentStep;

              return (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isCompleted
                          ? 'bg-cao-green text-white'
                          : isActive
                            ? 'bg-cao-blue text-white'
                            : 'bg-gray-800 text-gray-500'
                        }`}
                    >
                      {isCompleted ? (
                        <Check className="w-5 h-5" />
                      ) : (
                        <StepIcon className="w-5 h-5" />
                      )}
                    </div>
                    <span
                      className={`text-xs mt-2 whitespace-nowrap ${isActive ? 'text-cao-blue' : isCompleted ? 'text-cao-green' : 'text-gray-500'
                        }`}
                    >
                      {step.name}
                    </span>
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`w-8 sm:w-12 h-0.5 mx-1 ${isCompleted ? 'bg-cao-green' : 'bg-gray-700'
                        }`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Content Card */}
        <GlowingCard className="p-6 sm:p-8">
          {error && (
            <div className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-400 font-medium">Error</p>
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {renderStepContent()}
        </GlowingCard>
      </div>
    </div>
  );
}
